import { FC, useMemo } from 'react';
import {
  Bleed,
  formatDateTimeShort,
  InputDataDetailsType,
  OutputSnapshotVersionType,
  Padding,
  SubmittedDataVersionType,
  VersionTag,
  DataVersionStatus,
  formatDate,
  OutputVersionType,
  VersionDisplay,
  OutputVersionInputSourceType,
} from '@atlas-frontend/sdk';
import { Descriptions, Divider } from 'antd';
import { DescriptionsItemType } from 'antd/es/descriptions';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../InputDataSubjectDetail.translations.json';
import styled from 'styled-components';
import { fontSizeSM } from '@atlas-frontend/design-tokens';
import { InputDataVersionInfo } from '../types';
import { useInputDataDetailsStoreValue } from '../InputDataDetailsContext';

export interface InputComment {
  comments?: string | null;
  modifiedAt?: string | null;
  modifiedUsername?: string | null;
}

interface InputDataSubjectDetailLeftSideContentProps {
  inputDataDetailsType?: InputDataDetailsType;
  output?: OutputSnapshotVersionType;
  selectedSubmittedDataId?: number;
  selectedVersion?: SubmittedDataVersionType;
  versionData: InputDataVersionInfo;
  inputComment?: InputComment;
}

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-sample-detail',
);
addTranslations(translations);

export const InputDataSubjectDetailLeftSideContent: FC<
  InputDataSubjectDetailLeftSideContentProps
> = (props) => {
  const {
    inputDataDetailsType,
    output,
    selectedSubmittedDataId,
    selectedVersion,
    versionData,
    inputComment,
  } = props;
  const { t } = useTranslation();

  const [savedVersion] = useInputDataDetailsStoreValue('savedVersion');
  const selectedInputSource = output?.outputVersion.inputSources.find(
    (source) => source.submittedData.id === selectedSubmittedDataId,
  );
  const selectedSubmittedData = selectedInputSource?.submittedData;

  const groupItems: DescriptionsItemType[] = useMemo(
    () => [
      {
        key: 'dataObjectGroupCode',
        label: t('field.group.label'),
        children: inputDataDetailsType?.dataObjectGroupCode
          ? `${inputDataDetailsType?.dataObjectGroupCode} - ${inputDataDetailsType?.dataObjectGroupName}`
          : '',
      },
      {
        key: 'frequencyName',
        label: t('field.frequency.label'),
        children: inputDataDetailsType?.frequency?.name,
      },
      {
        key: 'dataManager',
        label: t('field.dataManager.label'),
        children: inputDataDetailsType?.dataManagerUsername,
      },
    ],
    [inputDataDetailsType, t],
  );

  const inputItems: DescriptionsItemType[] = useMemo(() => {
    const version = savedVersion?.updateInfo ?? selectedVersion;
    const isOutputVersion = versionData.version === 'output';
    return [
      {
        key: 'feature',
        label: t('field.feature.label'),
        children: isOutputVersion
          ? selectedSubmittedData?.statusName
          : version?.status?.name,
      },
      {
        key: 'modifiedBy',
        label: t('field.dataModifiedBy.label'),
        children: isOutputVersion
          ? selectedSubmittedData?.dataModifiedBy?.username
          : version?.dataModifiedBy?.username,
      },
      {
        key: 'modifiedAt',
        label: t('field.dataModifiedAt.label'),
        children: isOutputVersion
          ? formatDateTimeShort(selectedSubmittedData?.dataModifiedAt)
          : formatDateTimeShort(version?.dataModifiedAt),
      },
      ...(isOutputVersion && selectedInputSource
        ? [
            {
              key: 'inputVersion',
              label: t('field.inputVersion.label'),
              children: <InputVersion {...selectedInputSource} />,
            },
          ]
        : []),
    ];
  }, [
    savedVersion?.updateInfo,
    selectedInputSource,
    selectedSubmittedData?.dataModifiedAt,
    selectedSubmittedData?.dataModifiedBy?.username,
    selectedSubmittedData?.statusName,
    selectedVersion,
    t,
    versionData.version,
  ]);

  const outputVersionItems: DescriptionsItemType[] = useMemo(
    () => [
      {
        key: 'output',
        label: t('field.output.label'),
        children: output
          ? `${output.output.outputDescriptionCode} - ${output.output.outputDescriptionName}`
          : '',
      },
      {
        key: 'outputValueDate',
        label: t('field.outputValueDate.label'),
        children: output ? formatDate(output.outputDate.resultDoValueDate) : '',
      },
      {
        key: 'outputVersion',
        label: t('field.outputVersion.label'),
        children: output?.outputVersion ? (
          <OutputVersion version={output?.outputVersion} />
        ) : (
          ''
        ),
      },
    ],
    [output, t],
  );

  const inputCommentItems: DescriptionsItemType[] = useMemo(
    () => [
      {
        key: 'inputComment',
        label: t('field.inputComment.label'),
        children: <CommentSection {...inputComment} />,
      },
    ],
    [inputComment, t],
  );

  const subjectCommentItems: DescriptionsItemType[] = useMemo(
    () => [
      {
        key: 'subjectComment',
        label: t('field.subjectComment.label'),
        children: <CommentSection {...inputDataDetailsType?.subjectComments} />,
      },
    ],
    [inputDataDetailsType, t],
  );

  return (
    <Padding>
      <Descriptions items={groupItems} column={1} />
      <Bleed direction="horizontal" size="xl">
        <Divider />
      </Bleed>
      <Descriptions items={inputItems} column={1} />
      <Bleed direction="horizontal" size="xl">
        <Divider />
      </Bleed>
      {versionData.version === 'output' && (
        <>
          <Descriptions items={outputVersionItems} column={1} />
          <Bleed direction="horizontal" size="xl">
            <Divider />
          </Bleed>
        </>
      )}
      <Descriptions items={inputCommentItems} column={1} layout="vertical" />
      <Bleed direction="horizontal" size="xl">
        <Divider />
      </Bleed>
      <Descriptions items={subjectCommentItems} column={1} layout="vertical" />
    </Padding>
  );
};

interface CommentSectionProps {
  comments?: string | null;
  modifiedAt?: string | null;
  modifiedUsername?: string | null;
}

function CommentSection(props: CommentSectionProps) {
  const { t } = useTranslation();
  if (!props.comments) {
    return <span>{t('comments.missing')}</span>;
  }

  return (
    <div>
      <div>{props.comments}</div>
      <ChangedWrapper>{`${formatDateTimeShort(props.modifiedAt)} (${
        props.modifiedUsername
      })`}</ChangedWrapper>
    </div>
  );
}

const InputVersion = ({
  calculationTime,
  statusCode,
}: OutputVersionInputSourceType) => (
  <VersionTag className={statusCode}>
    {formatDateTimeShort(calculationTime)}{' '}
    <DataVersionStatus status={statusCode as DataVersionStatus} />
  </VersionTag>
);

const OutputVersion = ({ version }: { version: OutputVersionType }) => (
  <VersionDisplay
    versionNumber={version.outputVersionNumber}
    minorNumber={version.outputMinorNumber}
    status={version.outputStatusCode as DataVersionStatus}
  >
    {formatDate(version.calculationTime)}{' '}
    <DataVersionStatus status={version.outputStatusCode as DataVersionStatus} />
  </VersionDisplay>
);

const ChangedWrapper = styled.div`
  font-size: ${fontSizeSM}px;
  margin-top: 4px;
`;
